import React from "react";
import toast from "react-hot-toast";
import { TOKENS, FONT } from "./feedTokens";

/**
 * Rounded, tinted confirmation card (title + optional subtitle + dismiss)
 * used for the Feed share flow (copy link / download / share-image prep) —
 * distinct from the app's default dark toast so a successful share reads as
 * a deliberate, friendly confirmation rather than a generic system alert.
 */
const VARIANTS = {
  success: {
    bg: TOKENS.goodSoft,
    ring: TOKENS.good,
    title: "#0E4A34",
    subtitle: "#3E7A63",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    ),
  },
  error: {
    bg: "#FBEAE8",
    ring: TOKENS.bad,
    title: "#7A241C",
    subtitle: "#A85850",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    ),
  },
  loading: {
    bg: TOKENS.surface2,
    ring: TOKENS.muted,
    title: TOKENS.ink,
    subtitle: TOKENS.muted,
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
        <circle cx="12" cy="12" r="8" strokeOpacity="0.25" />
        <path d="M20 12a8 8 0 0 0-8-8" />
      </svg>
    ),
  },
};

function ShareToastCard({ t, variant, title, subtitle }) {
  const v = VARIANTS[variant] || VARIANTS.success;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        width: "320px",
        maxWidth: "88vw",
        background: v.bg,
        borderRadius: "16px",
        padding: "14px 12px 14px 12px",
        boxShadow: "0 10px 24px -8px rgba(15,23,42,0.25), 0 2px 6px rgba(15,23,42,0.08)",
        fontFamily: FONT,
        opacity: t.visible ? 1 : 0,
        transform: t.visible ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 150ms ease, transform 150ms ease",
      }}
    >
      <div
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          flexShrink: 0,
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: v.ring,
        }}
      >
        {v.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "13.5px", fontWeight: 700, color: v.title, lineHeight: 1.3 }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: "12px", fontWeight: 500, color: v.subtitle, marginTop: "2px", lineHeight: 1.35 }}>
            {subtitle}
          </div>
        )}
      </div>
      {variant !== "loading" && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => toast.dismiss(t.id)}
          style={{ background: "transparent", border: "none", cursor: "pointer", color: v.subtitle, flexShrink: 0, padding: "2px", marginTop: "2px" }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

const render = (variant, defaultDuration) => (title, subtitle, opts) =>
  toast.custom((t) => <ShareToastCard t={t} variant={variant} title={title} subtitle={subtitle} />, {
    duration: defaultDuration,
    ...opts,
  });

export const shareToast = {
  success: render("success", 3500),
  error: render("error", 4000),
  loading: render("loading", Infinity),
};
